import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Image, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface Product {
  id: string;
  name: string;
  image: string;
  category: string;
  variants?: string[];
}

interface FolderItem {
  id: string;
  name: string;
  type: 'folder' | 'image';
  path: string;
  image?: string;
}

interface ProductGalleryProps {
  onProductSelect?: (product: Product) => void;
  selectedProducts?: Product[];
  maxSelection?: number;
  showActions?: boolean;
}

const ProductGallery: React.FC<ProductGalleryProps> = ({
  onProductSelect,
  selectedProducts = [],
  maxSelection = 3,
  showActions = true
}) => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [folderItems, setFolderItems] = useState<FolderItem[]>([]);
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Función para navegar por carpetas
  const navigateToFolder = async (path: string) => {
    if (!user || !window.auth?.storage) {
      setError('Firebase Storage no está disponible');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('📁 Navegando a:', path);
      
      const storage = window.auth.storage;
      const listRef = window.auth.ref(storage, path);
      
      const res = await window.auth.listAll(listRef);
      console.log('📁 Items encontrados:', res.items.length, 'Carpetas:', res.prefixes.length);
      
      const items: FolderItem[] = [];
      
      // Procesar carpetas
      for (const prefix of res.prefixes) {
        items.push({
          id: prefix.name,
          name: prefix.name,
          type: 'folder',
          path: prefix.fullPath
        });
      }
      
      // Procesar archivos (solo imágenes)
      for (const itemRef of res.items) {
        const fileName = itemRef.name.toLowerCase();
        if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png')) {
          try {
            console.log('🖼️ Obteniendo URL para imagen:', itemRef.name);
            const url = await window.auth.getDownloadURL(itemRef);
            console.log('✅ URL obtenida:', url);
            items.push({
              id: itemRef.name,
              name: itemRef.name,
              type: 'image',
              path: itemRef.fullPath,
              image: url
            });
          } catch (err) {
            console.warn('❌ Error obteniendo URL para:', itemRef.name, err);
          }
        }
      }
      
      setFolderItems(items);
      setCurrentPath(path);
      
    } catch (error) {
      console.error('❌ Error navegando carpeta:', error);
      setError('Error al cargar la carpeta. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Función para ir hacia atrás
  const goBack = () => {
    if (pathHistory.length > 0) {
      const newHistory = [...pathHistory];
      const previousPath = newHistory.pop() || '';
      setPathHistory(newHistory);
      navigateToFolder(previousPath);
    }
  };

  // Función para seleccionar imagen
  const selectImage = (item: FolderItem) => {
    console.log('🖼️ Seleccionando imagen:', item);
    if (item.type === 'image' && item.image) {
      const product: Product = {
        id: item.id,
        name: item.name.split('.')[0], // Nombre sin extensión
        image: item.image,
        category: 'Ropa',
        variants: []
      };
      
      console.log('✅ Producto creado:', product);
      
      if (onProductSelect) {
        onProductSelect(product);
      }
    } else {
      console.log('❌ No se puede seleccionar:', item);
    }
  };

  // Función para remover producto seleccionado
  const handleProductRemove = (productId: string) => {
    // Esta función se puede implementar si es necesaria
    console.log('Remover producto:', productId);
  };

  // Cargar carpeta inicial
  useEffect(() => {
    if (user && window.auth?.storage) {
      const initialPath = `users/${user.uid}/tienda-ropa`;
      setPathHistory([]);
      navigateToFolder(initialPath);
    }
  }, [user]);

  return (
    <div className="space-y-4">
      {/* Navegación de carpetas */}
      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
        <button
          onClick={goBack}
          disabled={pathHistory.length === 0}
          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Atrás
        </button>
        <span className="text-gray-400">/</span>
        <span className="font-mono text-xs">{currentPath.split('/').pop() || 'tienda-ropa'}</span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-300">Cargando...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && folderItems.length === 0 && (
        <div className="text-center py-8">
          <Image className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Carpeta vacía</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            No hay carpetas o imágenes en esta ubicación
          </p>
        </div>
      )}

      {!loading && !error && folderItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {folderItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                item.type === 'folder'
                  ? 'border-blue-200 dark:border-blue-700 hover:border-blue-300 dark:hover:border-blue-600'
                  : selectedProducts.find(p => p.id === item.id)
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
              }`}
              onClick={() => {
                if (item.type === 'folder') {
                  setPathHistory([...pathHistory, currentPath]);
                  navigateToFolder(item.path);
                } else {
                  selectImage(item);
                }
              }}
            >
              <div className="aspect-square relative">
                {item.type === 'folder' ? (
                  <div className="w-full h-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <span className="text-white text-xl">📁</span>
                      </div>
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Carpeta</span>
                    </div>
                  </div>
                ) : item.image ? (
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.currentTarget as HTMLImageElement;
                      target.style.display = 'none';
                      const nextElement = target.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                
                {item.type === 'image' && !item.image && (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                    <Image className="w-8 h-8 text-gray-500" />
                  </div>
                )}
                
                {/* Indicador de selección para imágenes */}
                {item.type === 'image' && selectedProducts.find(p => p.id === item.id) && (
                  <div className="absolute top-2 right-2 bg-primary-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    ✓
                  </div>
                )}
                
                {/* Overlay de hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    {item.type === 'folder' ? (
                      <div className="bg-blue-500 text-white rounded-full p-2">
                        <span className="text-sm">📂</span>
                      </div>
                    ) : selectedProducts.find(p => p.id === item.id) ? (
                      <div className="bg-red-500 text-white rounded-full p-2">
                        <Eye className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className="bg-primary-500 text-white rounded-full p-2">
                        <Eye className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {item.name}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {item.type === 'folder' ? 'Carpeta' : 'Imagen'}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {selectedProducts.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Imágenes seleccionadas ({selectedProducts.length}/{maxSelection})
          </h4>
          <div className="flex flex-wrap gap-2">
            {selectedProducts.map((product) => (
              <div key={product.id} className="flex items-center space-x-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl p-3">
                <div className="w-12 h-12 bg-gray-200 dark:bg-slate-700 rounded-lg flex items-center justify-center overflow-hidden">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <Image className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{product.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{product.category}</p>
                </div>
                <button
                  onClick={() => handleProductRemove(product.id)}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;